import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { adminService } from '../../../services/adminService';
import {
  FEATURE_DEFINITIONS,
  FEATURE_CATEGORIES,
  formatLimit,
  formatStorageLimit,
  PLAN_DISPLAY,
  PLAN_ORDER,
} from '../../../config/planFeatures';
import {
  getCanonicalPricingTiers,
  getPricingTierKey,
} from '../../../config/pricingTiers';

// Info popup tooltip
const FeatureInfoPopup = ({ text }) => {
  const [show, setShow] = useState(false);
  if (!text) return null;
  return (
    <span className="relative inline-flex items-center ml-1">
      <button
        type="button"
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        className="w-4 h-4 rounded-full bg-muted border border-border text-muted-foreground text-xs flex items-center justify-center hover:bg-primary/10 hover:text-primary hover:border-primary/30 transition-colors"
        aria-label="More info"
      >
        i
      </button>
      {show && (
        <span className="absolute left-6 top-0 z-50 w-52 bg-gray-900 text-white text-xs rounded-lg px-3 py-2 shadow-xl pointer-events-none leading-relaxed">
          {text}
          <span className="absolute left-0 top-2 -translate-x-1 w-2 h-2 bg-gray-900 rotate-45" />
        </span>
      )}
    </span>
  );
};

const getPlanLimitsForCard = (plan) => ({
  maxForms: plan.maxForms,
  maxSubmissions: plan.maxSubmissions,
  maxTeamMembers: plan.maxTeamMembers,
  maxStorageMb: plan.maxStorageMb,
});

const getResolvedFeature = (plan, definition) => {
  const configured = plan?.featureEntitlements?.[definition.key];
  const saved = Array.isArray(plan?.features)
    ? plan.features.find((feature) => (feature?.key || feature?.featureKey) === definition.key)
    : null;
  const planKey = getPricingTierKey(plan);
  const included = typeof configured?.included === 'boolean'
    ? configured.included
    : saved?.included !== undefined
      ? saved.included !== false
      : definition.plans[planKey] !== false;

  return {
    included,
    value: configured?.value ?? saved?.value ?? (typeof definition.plans[planKey] === 'string' ? definition.plans[planKey] : ''),
    text: saved?.text || saved?.name || definition.label,
    infoPopup: saved?.infoPopup || saved?.info || saved?.tooltip || definition.description,
  };
};

// ─── Feature comparison table ────────────────────────────────────────────────
const COMPARE_LABELS = Object.fromEntries(PLAN_ORDER.map((key) => [key, PLAN_DISPLAY[key].label]));

const CellValue = ({ value }) => {
  if (value === true)  return <Icon name="Check" size={16} className="text-success mx-auto" />;
  if (value === false) return <Icon name="Minus" size={14} className="text-muted-foreground mx-auto" />;
  return <span className="text-xs font-medium text-foreground text-center block">{value}</span>;
};

const FeatureComparisonTable = ({ plans }) => {
  const [open, setOpen] = useState(false);
  const comparisonPlans = plans || [];

  return (
    <div className="mt-10 mb-6">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="mx-auto flex items-center gap-2 text-sm font-semibold text-primary hover:underline mb-4"
      >
        <Icon name={open ? 'ChevronUp' : 'ChevronDown'} size={16} />
        {open ? 'Hide' : 'See'} full feature comparison
      </button>

      {open && (
        <div className="overflow-x-auto rounded-2xl border-2 border-border shadow-sm">
          <table className="w-full min-w-[640px] text-sm border-collapse">
            <thead>
              <tr className="bg-muted/60">
                <th className="py-4 px-5 text-left font-semibold text-foreground w-1/3">Feature</th>
                {comparisonPlans.map((plan) => {
                  const planKey = getPricingTierKey(plan);
                  return (
                  <th key={planKey} className={`py-4 px-4 text-center font-bold ${planKey === 'professional' ? 'text-primary bg-primary/5' : 'text-foreground'}`}>
                    {plan.tierName || COMPARE_LABELS[planKey]}
                    {planKey === 'professional' && (
                      <span className="ml-1.5 text-xs bg-primary text-primary-foreground rounded-full px-2 py-0.5">Popular</span>
                    )}
                  </th>
                  );
                })}
              </tr>
              {/* Limit row */}
              <tr className="border-t border-border bg-muted/30">
                <td className="py-3 px-5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Limits</td>
                {comparisonPlans.map((plan) => {
                  const planKey = getPricingTierKey(plan);
                  const lim = getPlanLimitsForCard(plan);
                  return (
                    <td key={planKey} className={`py-3 px-4 text-center ${planKey === 'professional' ? 'bg-primary/5' : ''}`}>
                      <div className="space-y-0.5">
                        <div className="text-xs text-foreground font-medium">{formatLimit(lim.maxForms)} forms</div>
                        <div className="text-xs text-muted-foreground">{formatLimit(lim.maxSubmissions)} submissions/mo</div>
                        <div className="text-xs text-muted-foreground">{formatLimit(lim.maxTeamMembers)} {lim.maxTeamMembers === 1 ? 'seat' : 'seats'}</div>
                        <div className="text-xs text-muted-foreground">{formatStorageLimit(lim.maxStorageMb)} storage</div>
                      </div>
                    </td>
                  );
                })}
              </tr>
              <tr className="border-t border-border bg-card">
                <td className="py-3 px-5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Monthly price</td>
                {comparisonPlans.map((plan) => {
                  const planKey = getPricingTierKey(plan);
                  return (
                    <td key={planKey} className={`py-3 px-4 text-center text-sm font-semibold text-foreground ${planKey === 'professional' ? 'bg-primary/5' : ''}`}>
                      {Number(plan.price) === 0 ? 'Free' : `$${plan.price}/mo`}
                    </td>
                  );
                })}
              </tr>
              <tr className="border-t border-border bg-muted/20">
                <td className="py-3 px-5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Annual billing</td>
                {comparisonPlans.map((plan) => {
                  const planKey = getPricingTierKey(plan);
                  return (
                    <td key={planKey} className={`py-3 px-4 text-center ${planKey === 'professional' ? 'bg-primary/5' : ''}`}>
                      {Number(plan.annualPrice) === 0
                        ? <span className="text-sm font-semibold text-foreground">Free</span>
                        : <><span className="text-sm font-semibold text-foreground">${plan.annualMonthlyPrice}/mo</span><span className="block text-xs text-muted-foreground">${plan.annualPrice}/yr</span></>}
                    </td>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {FEATURE_CATEGORIES.map((cat) => {
                const rows = FEATURE_DEFINITIONS.filter((f) => f.category === cat);
                return (
                  <React.Fragment key={cat}>
                    <tr className="bg-muted/40">
                      <td colSpan={comparisonPlans.length + 1} className="py-2 px-5 text-xs font-bold text-muted-foreground uppercase tracking-wider">
                        {cat}
                      </td>
                    </tr>
                    {rows.map((feat, ri) => (
                      <tr key={feat.key} className={`border-t border-border/50 ${ri % 2 === 0 ? '' : 'bg-muted/10'} hover:bg-muted/20 transition-colors`}>
                        <td className="py-3 px-5 text-sm text-foreground font-medium leading-snug">
                          {getResolvedFeature(comparisonPlans[0], feat).text}
                          {getResolvedFeature(comparisonPlans[0], feat).infoPopup && (
                            <p className="text-xs text-muted-foreground font-normal mt-0.5 leading-relaxed">{getResolvedFeature(comparisonPlans[0], feat).infoPopup}</p>
                          )}
                        </td>
                        {comparisonPlans.map((plan) => {
                          const planKey = getPricingTierKey(plan);
                           const resolvedFeature = getResolvedFeature(plan, feat);
                          return (
                          <td key={planKey} className={`py-3 px-4 text-center ${planKey === 'professional' ? 'bg-primary/5' : ''}`}>
                             <CellValue value={resolvedFeature.included ? (resolvedFeature.value || true) : false} />
                             {(resolvedFeature.text !== feat.label || resolvedFeature.infoPopup !== feat.description) && (
                               <span className="mt-1 block text-[10px] text-muted-foreground leading-snug">
                                 {resolvedFeature.text}
                                 {resolvedFeature.infoPopup && <FeatureInfoPopup text={resolvedFeature.infoPopup} />}
                               </span>
                             )}
                          </td>
                          );
                        })}
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const FALLBACK_PLANS = getCanonicalPricingTiers();

const PricingSection = () => {
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [plans, setPlans] = useState(FALLBACK_PLANS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      const data = await adminService?.getAllPricingTiers?.();
       if (Array.isArray(data)) {
           setPlans(getCanonicalPricingTiers(data, { includeMissing: false }));
      }
    } catch {
      // Use fallback plans silently
    } finally {
      setLoading(false);
    }
  };

  const getCtaHref = (plan) => {
    if (!plan?.price && plan?.price !== 0) return '/authentication';
    const planKey = plan?.planKey || (PLAN_ORDER.includes(plan?.id) ? plan.id : null);
    return planKey ? `/signup?plan=${planKey}&billing=${billingCycle}` : '/authentication';
  };

  const isExternalLink = (_plan) => false;

  const getAnnualBillingDetails = (plan) => {
    const annualPrice = Number(plan?.annualPrice);
    const monthlyPrice = Number(plan?.price);
    if (!Number.isFinite(annualPrice) || !Number.isFinite(monthlyPrice)) return null;

    const monthlyEquivalent = Number.isFinite(Number(plan?.annualMonthlyPrice))
      ? Number(plan.annualMonthlyPrice)
      : annualPrice / 12;
    const savings = Math.max(0, monthlyPrice * 12 - annualPrice);

    return {
      annualPrice,
      monthlyEquivalent,
      savings,
      savingsPercent: monthlyPrice > 0 ? Math.round((savings / (monthlyPrice * 12)) * 100) : 0,
    };
  };

  const getDisplayPrice = (plan) => {
    if (plan?.price == null) return null; // null = custom pricing
    if (billingCycle === 'monthly') return plan?.price;
    return getAnnualBillingDetails(plan)?.monthlyEquivalent ?? plan?.price;
  };

  return (
    <section id="pricing" className="py-20 lg:py-32 bg-[#f5faf6] relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 dark:bg-primary/20 rounded-full text-sm font-medium text-primary mb-6">
            <Icon name="DollarSign" size={16} />
            <span>Pricing Plans</span>
          </div>
          <h2 className="text-4xl lg:text-6xl font-heading font-bold text-foreground mb-6">
            Simple, Transparent Pricing
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8 leading-relaxed">
            Choose the plan that fits your needs. Start on the free plan, then upgrade when you need more forms, submissions, or advanced features.
          </p>
           <p className="text-base sm:text-lg font-semibold text-primary max-w-4xl mx-auto mb-8 leading-relaxed">
             Less than one Typeform or Jotform plan, with unlimited seats, A/B testing, and analytics included from $29/mo.
           </p>

          {/* Billing Toggle */}
          <div className="inline-flex items-center gap-3 bg-card border-2 border-border rounded-xl p-1.5 shadow-sm">
            <button
              onClick={() => setBillingCycle('monthly')}
              aria-pressed={billingCycle === 'monthly'}
              className={`px-6 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                billingCycle === 'monthly' ?'bg-primary text-primary-foreground shadow-md' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('annual')}
              aria-pressed={billingCycle === 'annual'}
              className={`px-6 py-2.5 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 ${
                billingCycle === 'annual' ?'bg-primary text-primary-foreground shadow-md' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Annual
              <span className={`px-2 py-0.5 text-xs rounded-full font-bold ${
                billingCycle === 'annual'
                  ? 'bg-white/15 text-emerald-100'
                  : 'bg-success/20 text-success'
              }`}>
                Save up to 34% annually
              </span>
            </button>
          </div>
        </div>

        {/* Loading skeleton */}
        {loading && (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {[1, 2, 3, 4]?.map((i) => (
              <div key={i} className="bg-card border-2 border-border rounded-2xl p-8 animate-pulse">
                <div className="h-6 bg-muted rounded w-1/2 mb-3" />
                <div className="h-4 bg-muted rounded w-3/4 mb-6" />
                <div className="h-12 bg-muted rounded w-1/3 mb-6" />
                <div className="h-10 bg-muted rounded mb-6" />
                <div className="space-y-3">
                  {[1, 2, 3, 4, 5]?.map((j) => (
                    <div key={j} className="h-4 bg-muted rounded" />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pricing Cards */}
        {!loading && (
          <>
            <div className="grid items-stretch md:grid-cols-2 xl:grid-cols-4 gap-6 mb-6">
              {plans?.map((plan, index) => {
              const displayPrice = getDisplayPrice(plan);
              const annualBillingDetails = getAnnualBillingDetails(plan);
              const ctaHref = getCtaHref(plan);
              const external = isExternalLink(plan);
              const planKey = getPricingTierKey(plan);
              const cardFeatures = FEATURE_DEFINITIONS
                .map((feature) => {
                  const resolvedFeature = getResolvedFeature(plan, feature);
                  return {
                    key: feature.key,
                    text: resolvedFeature.text,
                    value: resolvedFeature.value,
                    infoPopup: resolvedFeature.infoPopup,
                    included: resolvedFeature.included,
                  };
                })
                .filter((feature) => feature.included);
              const visibleFeatures = cardFeatures.slice(0, 5);
              const additionalFeatureCount = cardFeatures.length - visibleFeatures.length;

              return (
                <div
                  key={plan?.id || index}
                  className={`relative flex h-full flex-col bg-card border-2 rounded-2xl p-6 lg:p-7 transition-all duration-300 ${
                    plan?.isPopular
                      ? 'border-primary shadow-xl'
                      : 'border-border hover:border-primary/30 hover:shadow-xl hover:-translate-y-1'
                  }`}
                >
                  {/* Badge */}
                  {plan?.badgeText && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <div className={`px-5 py-2 rounded-full text-sm font-bold shadow-lg ${
                        plan?.isPopular
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-card border-2 border-border text-foreground'
                      }`}>
                        {plan?.badgeText}
                      </div>
                    </div>
                  )}
                  {/* Plan Header */}
                  <div className="text-center">
                    <h3 className="text-2xl font-heading font-bold text-foreground mb-2">
                      {plan?.tierName}
                    </h3>
                    <p className="min-h-10 text-sm text-muted-foreground mb-4">
                      {plan?.description}
                    </p>

                    {/* Limit chips */}
                    {(() => {
                      const lim = getPlanLimitsForCard(plan);
                      return (
                        <div className="flex min-h-[74px] flex-wrap content-start justify-center gap-1.5 mb-5">
                          {[
                            { icon: 'FileText', label: `${formatLimit(lim.maxForms)} Forms` },
                            { icon: 'Inbox', label: `${formatLimit(lim.maxSubmissions)} Submissions/mo` },
                            { icon: 'Users', label: `${formatLimit(lim.maxTeamMembers)} ${lim.maxTeamMembers === 1 ? 'Seat' : 'Seats'}` },
                             { icon: 'HardDrive', label: formatStorageLimit(lim.maxStorageMb) },
                          ].map((chip, ci) => (
                            <span key={ci} className="inline-flex items-center gap-1 px-2.5 py-1 bg-muted border border-border rounded-full text-xs font-medium text-muted-foreground">
                              <Icon name={chip.icon} size={10} />
                              {chip.label}
                            </span>
                          ))}
                        </div>
                      );
                    })()}

                    {/* Price */}
                    <div className="mb-5">
                      {displayPrice != null ? (
                        <>
                          <div className="flex items-baseline justify-center gap-2 mb-2">
                            {displayPrice === 0 ? (
                              <span className="text-5xl font-heading font-bold text-foreground">Free</span>
                            ) : (
                              <>
                                <span className="text-5xl font-heading font-bold text-foreground">
                                  ${displayPrice}
                                </span>
                                <span className="text-muted-foreground font-medium">
                                  /month
                                </span>
                              </>
                            )}
                          </div>
                          {billingCycle === 'annual' && (
                            <p
                              className="min-h-10 text-xs text-success font-semibold leading-relaxed"
                              aria-label={`${plan?.tierName} annual billing details`}
                            >
                              {annualBillingDetails ? (
                                annualBillingDetails.annualPrice === 0 ? (
                                  'Free · $0/mo equivalent · $0/year'
                                ) : (
                                  <>
                                    Billed ${annualBillingDetails.annualPrice}/year
                                    {annualBillingDetails.savings > 0 && (
                                      <> · Save {annualBillingDetails.savingsPercent}% (${annualBillingDetails.savings})</>
                                    )}
                                  </>
                                )
                              ) : 'Annual billing available'}
                            </p>
                          )}
                        </>
                      ) : (
                        <div className="text-4xl font-heading font-bold text-foreground mb-2">
                          Contact Sales
                        </div>
                      )}
                    </div>

                    {/* CTA Button */}
                    {external ? (
                      <a
                        href={ctaHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-semibold text-sm transition-all ${
                          plan?.isPopular
                            ? 'bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/25'
                            : 'border-2 border-border text-foreground hover:border-primary/50 hover:bg-muted'
                        }`}
                      >
                        {planKey === 'free' ? 'Start free' : 'Contact sales'}
                        <Icon name="ExternalLink" size={14} />
                      </a>
                    ) : (
                      <Button
                        asChild
                        variant={plan?.isPopular ? 'default' : 'outline'}
                        size="lg"
                        fullWidth
                        className={plan?.isPopular ? 'shadow-lg shadow-primary/25' : ''}
                      >
                        <Link to={ctaHref}>
                          {planKey === 'free' ? 'Start free' : 'Get started'}
                        </Link>
                      </Button>
                    )}

                    {displayPrice && (
                      <p className="text-xs text-muted-foreground mt-3">
                        Free plan available
                      </p>
                    )}
                  </div>
                  {/* Features List */}
                  <div className="mt-6 space-y-3 pt-5 border-t-2 border-border">
                    <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-4">
                      What's Included:
                    </div>
                    {visibleFeatures.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-start gap-3">
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                          feature?.included
                            ? 'bg-success/10 dark:bg-success/20 border border-success/20' :'bg-muted'
                        }`}>
                          <Icon
                            name={feature?.included ? 'Check' : 'X'}
                            size={12}
                            className={feature?.included ? 'text-success' : 'text-muted-foreground'}
                          />
                        </div>
                        <span className={`text-sm leading-relaxed flex-1 ${
                          feature?.included ? 'text-foreground' : 'text-muted-foreground line-through'
                        }`}>
                          {feature?.text}
                          {feature?.value && <span className="ml-1 text-muted-foreground">({feature.value})</span>}
                          {(feature?.infoPopup || feature?.info) && <FeatureInfoPopup text={feature?.infoPopup || feature?.info} />}
                        </span>
                      </div>
                    ))}
                    {additionalFeatureCount > 0 && (
                      <p className="pl-8 text-xs font-medium text-primary">
                        +{additionalFeatureCount} more features in the comparison
                      </p>
                    )}
                  </div>
                </div>
              );
              })}
            </div>
            <p id="trial-guarantee" className="mb-16 scroll-mt-24 text-center text-sm sm:text-base font-medium text-foreground">
              14-day free trial on all paid plans. No credit card required.{' '}
              <Link to="/terms#refund" className="text-primary underline underline-offset-4 hover:text-primary/80">
                30-day money-back guarantee.
              </Link>
            </p>
          </>
        )}

        {/* Feature comparison table */}
      <FeatureComparisonTable plans={plans} />

        {/* Additional Info */}
        <div className="bg-muted/50 border-2 border-border rounded-2xl p-8 text-center">
          <div className="flex flex-wrap justify-center gap-8 mb-6">
            <div className="flex items-center gap-2 text-foreground">
              <Icon name="Shield" size={20} className="text-success" />
              <span className="text-sm font-medium">SSL Encryption</span>
            </div>
            <div className="flex items-center gap-2 text-foreground">
              <Icon name="FileCheck" size={20} className="text-success" />
              <span className="text-sm font-medium">Privacy-First</span>
            </div>
            <div className="flex items-center gap-2 text-foreground">
              <Icon name="Clock" size={20} className="text-success" />
              <span className="text-sm font-medium">Reliable Uptime</span>
            </div>
            <div className="flex items-center gap-2 text-foreground">
              <Icon name="Headphones" size={20} className="text-success" />
              <span className="text-sm font-medium">24/7 Support</span>
            </div>
          </div>
          <p className="text-muted-foreground">
            All plans include encrypted data handling, access controls, and support. Need a custom plan?{' '}
            <Link to="/authentication" className="text-primary hover:underline font-medium">
              Contact our sales team
            </Link>
          </p>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;